import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  alpha,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  Send as SendIcon,
  Celebration as CelebrationIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/firebase/auth.service';

interface OnboardingStep {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  image: string;
  color: string;
}

const steps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to ShiftGrid!',
    subtitle: 'Your scheduling journey starts here',
    description: "We're excited to have you on board. Let's take a quick tour to help you get the most out of ShiftGrid and set up your organization for success.",
    icon: <CelebrationIcon sx={{ fontSize: 32 }} />,
    image: '/images/onboarding/welcome.webp',
    color: '#6366F1', // Indigo
  },
  {
    id: 'shops',
    title: 'Add Your Shops',
    subtitle: 'Step 1 of 4',
    description: 'Start by adding your shop locations. Each shop has its own operating hours, coverage requirements, and team. You can manage multiple locations from one dashboard.',
    icon: <StoreIcon sx={{ fontSize: 32 }} />,
    image: '/images/onboarding/shops.webp',
    color: '#8B5CF6', // Purple
  },
  {
    id: 'employees',
    title: 'Build Your Team',
    subtitle: 'Step 2 of 4',
    description: 'Add your employees with their contact information and assign them to shops. Employees can work at multiple locations, and you can track their skills and preferences.',
    icon: <PeopleIcon sx={{ fontSize: 32 }} />,
    image: '/images/onboarding/employees.webp',
    color: '#EC4899', // Pink
  },
  {
    id: 'schedules',
    title: 'Create Schedules',
    subtitle: 'Step 3 of 4',
    description: 'Build weekly schedules by assigning employees to shifts. ShiftGrid automatically shows you coverage gaps, scheduling conflicts, and employee availability to help you plan better.',
    icon: <CalendarIcon sx={{ fontSize: 32 }} />,
    image: '/images/onboarding/schedules.webp',
    color: '#F59E0B', // Amber
  },
  {
    id: 'invite',
    title: 'Invite Your Team',
    subtitle: 'Step 4 of 4',
    description: 'Send invitation links to your employees so they can create accounts, view their schedules on any device, and set their availability. Everyone stays in sync!',
    icon: <SendIcon sx={{ fontSize: 32 }} />,
    image: '/images/onboarding/invite.webp',
    color: '#10B981', // Emerald
  },
];

interface OwnerOnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

export default function OwnerOnboardingModal({ open, onComplete }: OwnerOnboardingModalProps) {
  const { user, updateUserProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    if (!user?.uid) return;

    try {
      setIsCompleting(true);

      if (dontShowAgain) {
        await authService.completeOnboarding(user.uid, 'owner');
        await updateUserProfile({ onboardingCompleted: true, onboardingType: 'owner' });
      }

      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      onComplete();
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', minHeight: 500 }}>
          {/* Left Side - Image/Illustration */}
          <Box
            sx={{
              width: '45%',
              bgcolor: alpha(step.color, 0.1),
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background decoration */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: alpha(step.color, 0.1),
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: alpha(step.color, 0.15),
              }}
            />

            {/* Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                bgcolor: step.color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: `0 8px 32px ${alpha(step.color, 0.3)}`,
              }}
            >
              {step.icon}
            </Box>

            {/* Placeholder Image */}
            <Box
              component="img"
              src={step.image}
              alt={step.title}
              onError={(e) => {
                // Hide image if not found
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              sx={{
                maxWidth: '100%',
                maxHeight: 200,
                objectFit: 'contain',
                borderRadius: 2,
                zIndex: 1,
              }}
            />

            {/* Step indicators */}
            <Box sx={{ display: 'flex', gap: 1, mt: 4, zIndex: 1 }}>
              {steps.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: index === currentStep ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: index === currentStep ? step.color : alpha(step.color, 0.3),
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Right Side - Content */}
          <Box
            sx={{
              flex: 1,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Mobile step indicator */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mb: 3, justifyContent: 'center' }}>
              {steps.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: index === currentStep ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: index === currentStep ? step.color : alpha(step.color, 0.3),
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>

            {/* Step subtitle */}
            <Typography
              variant="overline"
              sx={{
                color: step.color,
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1,
              }}
            >
              {step.subtitle}
            </Typography>

            {/* Title */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              {step.title}
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.7,
                mb: 4,
                flex: 1,
              }}
            >
              {step.description}
            </Typography>

            {/* Mobile icon */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: step.color,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {step.icon}
              </Box>
            </Box>

            {/* Don't show again checkbox (only on last step) */}
            {isLastStep && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dontShowAgain}
                    onChange={(e) => setDontShowAgain(e.target.checked)}
                    sx={{
                      color: step.color,
                      '&.Mui-checked': {
                        color: step.color,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Don't show this again
                  </Typography>
                }
                sx={{ mb: 2 }}
              />
            )}

            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {!isFirstStep && (
                <IconButton
                  onClick={handleBack}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleNext}
                disabled={isCompleting}
                endIcon={!isLastStep && <ArrowForwardIcon />}
                sx={{
                  flex: 1,
                  py: 1.5,
                  bgcolor: step.color,
                  '&:hover': {
                    bgcolor: alpha(step.color, 0.9),
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                {isLastStep ? "Let's Get Started!" : 'Continue'}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
